import { PushPin } from '@phosphor-icons/react';
import type { PersonColor } from '@shh/design-tokens';
import { Button as AriaButton } from 'react-aria-components';
import { Icon } from '../atoms/Icon';
import { pressable } from '../lib/card';
import { cn } from '../lib/cn';
import { personInk, personSoft } from '../lib/person';

export interface NoteCardProps {
  text: string;
  /** Who pinned it ("Mama") – tints the note in their colour. */
  author?: string;
  color?: PersonColor;
  /** "bis Sonntag" */
  until?: string;
  onPress?: () => void;
  /** Pinboard rows: at most two lines – the full text is one tap away. */
  clamp?: boolean;
  /**
   * strip = one slim line (text, then who) – the pinboard on "Heute" since 1.10, so the day plan
   * stays high up. The full note is one tap away.
   */
  variant?: 'card' | 'strip';
  className?: string;
}

/**
 * A note on the family pinboard: the text big, who pinned it small. Tinted in the author's
 * colour so the family sees at a glance who wrote what. Tappable when it can be changed.
 */
export function NoteCard({
  text,
  author,
  color = 'sun',
  until,
  onPress,
  clamp,
  variant = 'card',
  className,
}: NoteCardProps) {
  const strip = variant === 'strip';
  const body = strip ? (
    <>
      <Icon icon={PushPin} size="sm" weight="fill" className={cn('shrink-0', personInk[color])} />
      <span className="min-w-0 flex-1 truncate font-semibold text-text-primary">{text}</span>
      {(author || until) && (
        <span className="shrink-0 text-sm text-text-secondary">
          {[author, until].filter(Boolean).join(' · ')}
        </span>
      )}
    </>
  ) : (
    <>
      <span className="flex items-start gap-gap-xs">
        <Icon
          icon={PushPin}
          size="sm"
          weight="fill"
          className={cn('mt-1 shrink-0', personInk[color])}
        />
        <span
          className={cn(
            'text-md font-semibold hyphens-auto break-words text-text-primary',
            clamp && 'line-clamp-2',
          )}
        >
          {text}
        </span>
      </span>
      {(author || until) && (
        <span className="text-sm text-text-secondary">
          {[author, until].filter(Boolean).join(' · ')}
        </span>
      )}
    </>
  );
  const look = cn(
    strip
      ? 'flex w-full items-center gap-gap-sm rounded-control px-inset-sm py-inset-xs text-left'
      : 'flex w-full flex-col gap-1 rounded-card p-inset-sm text-left',
    personSoft[color],
    className,
  );
  return onPress ? (
    <AriaButton
      onPress={onPress}
      className={cn(
        look,
        pressable,
        'min-h-touch-min cursor-pointer outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
      )}
    >
      {body}
    </AriaButton>
  ) : (
    <div className={look}>{body}</div>
  );
}
